import { useState, useContext, useCallback, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CardContext } from 'context/cards/cardContext';
import { HeaderComponent } from 'components/shared/HeaderComponent';
import { OPEN_CREATE_NEW_CARD, UPDATE_CARDS_METADATA, OPEN_REQUEST_NEW_CARD } from 'context/cards/types';
import AlertDialog from 'components/shared/AlertDialog/AlertDialog';
import BlueToolTip from 'components/shared/BlueTooltip';
import CardsTable from './table/CardsTable';
import CreateCard from './CreateCard/CreateCard';
import EditCard from './EditCard/EditCard';
import EditCardFunds from './EditCardFunds/EditCardFunds';
import NoBalanceDialog from './dialogs/NoBalanceDialog';
import RequestCard from './RequestCard/RequestCard';
import RequestEditLimitModal from './RequestEditLimitModal/RequestEditLimitModal';
import ReviewCard from './ReviewCard/ReviewCard';
import StatusSnackbar from 'components/shared/StatusSnackbar';
import useCardSettings from 'Hooks/useCardSettings';
// Services
import { Overview } from 'lib/api';
// Utils and Helpers
import { hasPINModel, hasCreditModel, hasAvailableFunds, hasCreditLine } from 'helpers/businesses';
import { isAdminUser, isCardHolder } from 'helpers/auth';
import { NO_FUNDS_TEXT, NO_FUNDS_TITLE } from 'helpers/constants';

const { REACT_APP_AR_FRONTEND_URL } = process.env;

const CardSection = () => {
    const { dispatch, statistics } = useContext(CardContext);
    const { userInfo, hasAccount, businessInfo } = useContext(AuthContext);
    const [snackbarLabel, setSnackbarLabel] = useState('');
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
    const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
    const cardSettings = useCardSettings();

    const fetchOverviewInfo = useCallback(async () => {
        try {
            const response = await Overview.getOverviewInfo();
            dispatch({
                type: UPDATE_CARDS_METADATA,
                payload: response
            });
        } catch (error) {
            console.error(error);
        }
    }, [dispatch]);
    useEffect(() => {
        if (userInfo.kyb === 'approved') {
            fetchOverviewInfo();
        }
    }, [fetchOverviewInfo, userInfo]);

    const handleCreateCardClick = () => {
        if (!hasAvailableFunds(businessInfo, statistics)) {
            return setIsAlertDialogOpen(true);
        }
        if (hasAccount) {
            return dispatch({ type: OPEN_CREATE_NEW_CARD });
        }

        setSnackbarLabel('KYC / KYB approval required');
        setIsSnackbarOpen(true);
    };

    const handleRequestCardClick = () => {
        if (!hasAccount) {
            setSnackbarLabel('KYC / KYB approval required');
            setIsSnackbarOpen(true);
        }
        return dispatch({ type: OPEN_REQUEST_NEW_CARD });
    };

    const onAlertButtonClickEvent = () => {
        if (hasPINModel(businessInfo)) {
            window.open(`${REACT_APP_AR_FRONTEND_URL}/v2/ar/customers/${businessInfo.business_ar_id}/overview`, '_blank');
        }
        setIsAlertDialogOpen(false);
    };

    const isCreateCardDisabled =
        businessInfo?.is_frozen ||
        businessInfo?.is_disabled ||
        (hasPINModel(businessInfo) && !Number(statistics?.accountBalance)) ||
        !cardSettings.isCreationEnabled;

    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" component="div">
                    <HeaderComponent title="Cards" />
                </Typography>
                {isCardHolder(userInfo?.role?.id) && (
                    <Button variant="contained" onClick={handleRequestCardClick}>
                        Request Card
                    </Button>
                )}
                {isAdminUser(userInfo?.role?.id) && isCreateCardDisabled && (
                    <BlueToolTip
                        placement="left"
                        arrow
                        title="The card creation service is temporarily unavailable due to system maintenance.">
                        <span>
                            <Button
                                onClick={handleCreateCardClick}
                                variant="contained"
                                data-testid="cards-create-card-button"
                                disabled>
                                Create Card
                            </Button>
                        </span>
                    </BlueToolTip>
                )}
                {isAdminUser(userInfo?.role?.id) && !isCreateCardDisabled && (
                    <Button onClick={handleCreateCardClick} variant="contained" data-testid="cards-create-card-button">
                        Create Card
                    </Button>
                )}
            </Box>
            <CardsTable />
            <CreateCard />
            <RequestCard />
            <ReviewCard />
            <EditCard />
            <EditCardFunds />
            <RequestEditLimitModal />

            <AlertDialog
                isOpen={isAlertDialogOpen}
                title={
                    hasCreditModel(businessInfo) && hasCreditLine(businessInfo)
                        ? NO_FUNDS_TITLE.CREDIT_MODEL
                        : NO_FUNDS_TITLE.PREPAID_MODEL
                }
                description={
                    hasCreditModel(businessInfo) && hasCreditLine(businessInfo)
                        ? NO_FUNDS_TEXT.CREDIT_MODEL
                        : NO_FUNDS_TEXT.PREPAID_MODEL
                }
                onButtonClick={onAlertButtonClickEvent}
                buttonText={hasPINModel(businessInfo) ? 'Add Funds' : 'OK'}
            />
            <NoBalanceDialog />
            <StatusSnackbar label={snackbarLabel} severity={'error'} open={isSnackbarOpen} setOpenAlert={setIsSnackbarOpen} />
        </>
    );
};

export default CardSection;
